import { useState, useEffect, useRef, type KeyboardEventHandler } from 'react'

type DateFormat = 'us' | 'iso'

interface DateInputProps {
  value: string
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
  placeholder?: string
  /** Display and typing format. `us` = mm/dd/yyyy (default), `iso` = yyyy-mm-dd. */
  dateFormat?: DateFormat
  /** Runs after blur parsing and parent onChange, deferred so parent state is committed before autosave flush. */
  onCommitBlur?: () => void
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  'aria-label'?: string
}

function isValidDate(isoDate: string): boolean {
  if (!isoDate) return false
  const date = new Date(isoDate)
  return !isNaN(date.getTime())
}

function formatDateForDisplay(isoDate: string, dateFormat: DateFormat): string {
  if (!isoDate) return ''
  const parts = isoDate.split('-')
  if (parts.length !== 3) return isoDate
  const [year, month, day] = parts
  if (dateFormat === 'iso') return `${year}-${month}-${day}`
  return `${month}/${day}/${year}`
}

function parseUsDisplay(displayDate: string): string {
  if (!displayDate) return ''
  const cleaned = displayDate.replace(/[^\d/]/g, '')
  const parts = cleaned.split('/')
  if (parts.length !== 3) return ''
  const [month, day, year] = parts
  if (!month || !day || !year) return ''
  if (year.length !== 4) return ''
  const m = month.padStart(2, '0')
  const d = day.padStart(2, '0')
  return `${year}-${m}-${d}`
}

function parseUsDisplayOnBlur(displayDate: string): string {
  if (!displayDate) return ''
  const cleaned = displayDate.replace(/[^\d/]/g, '')
  const parts = cleaned.split('/').filter((p) => p.length > 0)
  if (parts.length !== 3) return ''
  let [month, day, year] = parts
  if (!month || !day || !year) return ''
  month = month.padStart(2, '0')
  day = day.padStart(2, '0')
  if (year.length === 2) {
    year = `20${year}`
  }
  if (year.length !== 4) return ''
  return `${year}-${month}-${day}`
}

function parseIsoDisplay(displayDate: string): string {
  if (!displayDate) return ''
  const cleaned = displayDate.replace(/[^\d-]/g, '')
  const parts = cleaned.split('-')
  if (parts.length !== 3) return ''
  const [year, month, day] = parts
  if (!year || !month || !day) return ''
  if (year.length !== 4) return ''
  const m = month.padStart(2, '0')
  const d = day.padStart(2, '0')
  return `${year}-${m}-${d}`
}

function parseIsoDisplayOnBlur(displayDate: string): string {
  if (!displayDate) return ''
  const cleaned = displayDate.replace(/[^\d-]/g, '')
  const parts = cleaned.split('-').filter((p) => p.length > 0)
  if (parts.length !== 3) return ''
  let [year, month, day] = parts
  if (!year || !month || !day) return ''
  if (year.length === 2) {
    year = `20${year}`
  }
  if (year.length !== 4) return ''
  month = month.padStart(2, '0')
  day = day.padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatPartialUsDigits(digits: string, digitsJustGrew: boolean): string {
  if (digits.length === 0) return ''
  if (digits.length <= 2) {
    if (digits.length === 2 && digitsJustGrew) {
      return `${digits}/`
    }
    return digits
  }
  if (digits.length <= 4) {
    const mid = `${digits.slice(0, 2)}/${digits.slice(2)}`
    if (digits.length === 4 && digitsJustGrew) {
      return `${mid}/`
    }
    return mid
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
}

function formatPartialIsoDigits(digits: string, digitsJustGrew: boolean): string {
  if (digits.length === 0) return ''
  if (digits.length <= 4) {
    if (digits.length === 4 && digitsJustGrew) {
      return `${digits}-`
    }
    return digits
  }
  if (digits.length <= 6) {
    const mid = `${digits.slice(0, 4)}-${digits.slice(4)}`
    if (digits.length === 6 && digitsJustGrew) {
      return `${mid}-`
    }
    return mid
  }
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`
}

export default function DateInput({
  value,
  onChange,
  className = '',
  disabled = false,
  placeholder,
  dateFormat = 'us',
  onCommitBlur,
  onKeyDown,
  'aria-label': ariaLabel,
}: DateInputProps) {
  const resolvedPlaceholder = placeholder ?? (dateFormat === 'iso' ? 'yyyy-mm-dd' : 'mm/dd/yyyy')
  const separator = dateFormat === 'iso' ? '-' : '/'

  const [displayValue, setDisplayValue] = useState(() => formatDateForDisplay(value, dateFormat))
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const prevDigitsRef = useRef('')

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatDateForDisplay(value, dateFormat))
    }
  }, [value, isFocused, dateFormat])

  function formatPartialDigits(digits: string, digitsJustGrew: boolean): string {
    return dateFormat === 'iso'
      ? formatPartialIsoDigits(digits, digitsJustGrew)
      : formatPartialUsDigits(digits, digitsJustGrew)
  }

  function parseDisplay(displayDate: string): string {
    return dateFormat === 'iso' ? parseIsoDisplay(displayDate) : parseUsDisplay(displayDate)
  }

  function parseDisplayOnBlur(displayDate: string): string {
    return dateFormat === 'iso'
      ? parseIsoDisplayOnBlur(displayDate)
      : parseUsDisplayOnBlur(displayDate)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    const isDeletion = raw.length < displayValue.length
    const cleaned = raw.replace(dateFormat === 'iso' ? /[^\d-]/g : /[^\d/]/g, '')
    let digits = cleaned.replace(dateFormat === 'iso' ? /-/g : /\//g, '')

    if (dateFormat === 'us') {
      if (digits.length === 1 && /\//.test(cleaned)) {
        digits = digits.padStart(2, '0')
      }
      if (digits.length === 3 && /\//.test(cleaned) && cleaned.endsWith('/')) {
        digits = digits.slice(0, 2) + digits.slice(2).padStart(2, '0')
      }
    } else {
      if (digits.length === 5 && /-/.test(cleaned) && cleaned.endsWith('-')) {
        digits = digits.slice(0, 4) + digits.slice(4).padStart(2, '0')
      }
      if (digits.length === 7 && /-/.test(cleaned) && cleaned.endsWith('-')) {
        digits = digits.slice(0, 6) + digits.slice(6).padStart(2, '0')
      }
    }

    const prevDigits = prevDigitsRef.current
    const digitsJustGrew = digits.length > prevDigits.length
    const digitsUnchanged = digits === prevDigits
    prevDigitsRef.current = digits

    const redundantTypedSeparator =
      !isDeletion &&
      digitsUnchanged &&
      (dateFormat === 'iso' ? /-/.test(cleaned) : /\//.test(cleaned)) &&
      (dateFormat === 'iso'
        ? digits.length === 4 || digits.length === 6
        : digits.length === 2 || digits.length === 4)

    const formatted = formatPartialDigits(digits, digitsJustGrew || redundantTypedSeparator)

    setDisplayValue(formatted)

    if (formatted.length === 10) {
      const isoDate = parseDisplay(formatted)
      if (isoDate && isValidDate(isoDate)) {
        onChange(isoDate)
      }
    } else if (formatted === '') {
      onChange('')
    }
  }

  function handleBlur() {
    setIsFocused(false)
    if (displayValue === '') {
      onChange('')
      if (onCommitBlur) {
        setTimeout(onCommitBlur, 0)
      }
      return
    }
    const isoFromBlur = parseDisplayOnBlur(displayValue)
    const isoDate =
      isoFromBlur && isValidDate(isoFromBlur) ? isoFromBlur : parseDisplay(displayValue)
    if (isoDate && isValidDate(isoDate)) {
      onChange(isoDate)
      setDisplayValue(formatDateForDisplay(isoDate, dateFormat))
    } else {
      setDisplayValue(formatDateForDisplay(value, dateFormat))
    }
    if (onCommitBlur) {
      setTimeout(onCommitBlur, 0)
    }
  }

  function handleFocus() {
    setIsFocused(true)
    prevDigitsRef.current = displayValue.replace(/\D/g, '')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(e)
    if (e.defaultPrevented) return
    if (e.key !== separator || disabled) return
    const el = inputRef.current
    if (!el) return
    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? 0
    if (start !== end) return

    const v = displayValue

    if (start < v.length && v[start] === separator) {
      e.preventDefault()
      const next = start + 1
      requestAnimationFrame(() => el.setSelectionRange(next, next))
      return
    }

    if (start > 0 && start === v.length && v[start - 1] === separator) {
      e.preventDefault()
    }
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      aria-label={ariaLabel}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={resolvedPlaceholder}
      disabled={disabled}
      className={className}
      maxLength={10}
    />
  )
}
